import { useCallback, useMemo, useState } from 'react';

import {
  ArrowDown01Icon,
  Cancel01Icon,
  ComputerIcon,
  Download01Icon,
  File02Icon,
  ViewIcon,
} from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { Permissions } from 'constants/permissions';
import { RESULT_OPTIONS } from 'constants/result-options';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { screeningVerificationBadge } from 'helpers/screeningVerificationBadge';
import { useAccessControl } from 'hooks/access-control';
import { useLocale } from 'hooks/locale';
import { Collapse } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';

import { Select } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import { StatusPill } from 'components/Pills/StatusPills';
import { CustomToast } from 'components/Toast/CustomToast';

import Alert from '../Alert/Alert';
import { KeyValueModal } from '../KeyValueModal/KeyValueModal';
import { MetadataDetailsModal } from '../MetadataDetailsModal/MetadataDetailsModal';
import styles from './CheckStatusRow.module.scss';

export const CheckStatusRow = ({
  candidate,
  checkType,
  companyCheck,
  company,
  check,
  permissions,
  useViewResult = true,
  useDownloadResult = true,
  user,
  setLoading = () => {},
  handleSubCheckUpdate = () => {},
  getCandidateCheckResult = () => {},
  handleCancelCheck = () => {},
  getSubCheckResult = () => {},
  handleCheckUpdate = () => {},
}) => {
  const [showMetadata, setShowMetadata] = useState(false);
  const [showExtraDetails, setShowExtraDetails] = useState(false);

  const [selectedSubCheck, setSelectedSubCheck] = useState(null);
  const [showSubExtraDetails, setSubShowExtraDetails] = useState(false);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [subChecksOpened, setSubChecksOpened] = useState(false);
  const { dateSlashFormat } = useLocale(company?.locale);

  const hasAccess = useAccessControl(permissions, { notPermissionPolicy: 'allowed' });

  const { t } = useTranslation();

  const onCancelCheck = checkId => {
    setShowCancelModal(false);
    handleCancelCheck(checkId);
  };

  const onChangeStatus = (checkId, statusValue) => {
    if (check.user_review_status === statusValue) return;

    handleCheckUpdate(checkId, { user_review_status: statusValue });
  };

  const onChangeSubCheckStatus = (subCheckId, statusValue) => {
    handleSubCheckUpdate(subCheckId, { user_review_status: statusValue });
  };

  const checkViewResult = (model, model_type) => {
    if (!model?.upload_id) {
      showToast('There is no result to view', 'info');
      return;
    }

    const uri = `${process.env.REACT_APP_WEB_URL}/documents/${model.upload_id}/${model_type}/${model.id}`;
    window.open(uri, '_blank').focus();
  };

  const downloadResult = async () => {
    setLoading(true);
    try {
      await getCandidateCheckResult({
        check,
        candidate,
        isFile: checkType.result_type === RESULT_OPTIONS.File,
      }).unwrap();
    } catch (error) {
      showToast('Oops Error Downloading the File ');
      console.error('errow downloading sub check result file : ', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadSubCheckResult = async subCheck => {
    try {
      setLoading(true);
      await getSubCheckResult({
        subCheck,
        check,
        candidate,
        isFile: checkType.result_type === RESULT_OPTIONS.File,
      }).unwrap();
    } catch (error) {
      showToast('Oops Error Downloading the File ');
      console.error('errow downloading sub check result file : ', error);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (title, type = 'error') => {
    toast.custom(
      t => (
        <CustomToast
          t={t}
          title={title}
          message={`Please try again or if it continues, please contact the support team.`}
          type={type}
        />
      ),
      {
        duration: 5000,
      },
    );
  };

  const canAccessResults = useCallback(
    (view = false) => {
      if (companyCheck.has_sensitive_results) {
        return hasAccess(Permissions.CandidateChecksSensitiveResults);
      }

      return view
        ? hasAccess(Permissions.CandidateChecksResultsRead)
        : hasAccess(Permissions.CandidateChecksResultsDownload);
    },
    [companyCheck, user],
  );

  const getExtraDetails = checkExtraData => {
    if (!checkExtraData) return null;

    let filteredExtraData = {};
    Object.keys(checkExtraData).forEach(key => {
      if (checkType.extra_reporting_columns?.includes(key)) {
        filteredExtraData[key] = checkExtraData[key];
      }
    });

    companyCheck.extra_result_fields?.forEach(field => {
      filteredExtraData[field] = checkExtraData[field];
    });

    return filteredExtraData;
  };

  const hasExtraDetails = useMemo(() => {
    if (!check.extra_data) return false;
    const extraDetails = getExtraDetails(check.extra_data);
    return extraDetails && Object.keys(extraDetails).length > 0;
  }, [check.extra_data, checkType.extra_reporting_columns, companyCheck.extra_result_fields]);

  const openSubCheckExtraDetailsModal = subCheck => {
    setSelectedSubCheck(subCheck);
    setSubShowExtraDetails(true);
  };

  return (
    <div className={cn(styles.root, { [styles.rootDummy]: check.dummy })}>
      <Alert
        show={showCancelModal}
        title={t('check.backgroundChecks.cancelCheckTitle', { checkType: checkType.title })}
        ok={t('check.backgroundChecks.cancelCheckOk')}
        cancel={t('check.backgroundChecks.cancelCheckCancel')}
        onOk={() => {
          onCancelCheck(check.id);
        }}
        onCancel={() => {
          setShowCancelModal(false);
        }}
      />

      <div className={styles.checkRow}>
        <MetadataDetailsModal
          visible={showMetadata}
          onClose={() => setShowMetadata(false)}
          userAgent={check.user_agent}
          country={check.country}
        />
        {hasExtraDetails && (
          <KeyValueModal
            visible={showExtraDetails}
            onClose={() => setShowExtraDetails(false)}
            title="More Details"
            data={getExtraDetails(check.extra_data)}
          />
        )}

        <div className={cn(styles.divider, 'u-divider')} />

        <div className={cn(styles.logoColumn)}>
          <IconComponent icon={getCheckTypeIcon(checkType?.slug)} />
        </div>

        <div className={cn(styles.titleColumn)}>
          <div className="flex flex-col">
            <div className="title-4 u-text-ellipsis">{checkType.title}</div>
            {check?.sub_checks?.length > 0 && check?.type === 'candidate_questionnaire' && (
              <div className="text-text-tertiary text-lg font-regular">
                {check.sub_checks
                  .map(s => s?.title)
                  .filter(Boolean)
                  .join(', ')}
              </div>
            )}
          </div>
          <StatusPill
            status={check.status}
            className="u-capitalize"
            text={
              <>
                {check.status}
                {(check.status == 'submitted' || check.status == 'in-progress') &&
                  ` - ${check.in_progress_sub_status.replaceAll('_', ' ').capitalize()}`}
              </>
            }
          />
        </div>

        <div className={cn(styles.createdOnColumn)}>
          <div className={styles.dateTitle}>{t('check.backgroundChecks.columns.createdOn')}</div>
          <div className={styles.dateValue}>
            <Moment format={dateSlashFormat}>{check.created_at}</Moment>
          </div>
        </div>

        <div className={cn(styles.completedOnColumn)}>
          <div className={styles.dateTitle}>{t('check.backgroundChecks.columns.completedOn')}</div>
          <div className={styles.dateValue}>
            {check.completed_at ? (
              <Moment format={dateSlashFormat}>{check.completed_at}</Moment>
            ) : (
              '-'
            )}
          </div>
        </div>

        <div className={cn(styles.expiredOnColumn)}>
          {checkType.has_expiration_date && (
            <>
              <div className={styles.dateTitle}>
                {t('check.backgroundChecks.columns.expiresOn')}
              </div>
              <div className={styles.dateValue}>
                <Moment format={dateSlashFormat} parse="YYYY-MM-DD">
                  {check.expiration_date}
                </Moment>
              </div>
            </>
          )}
        </div>

        <div className={cn(styles.divider2, 'u-divider')} />

        <div className={cn(styles.statusColumn)}>
          {screeningVerificationBadge(check, checkType)}
        </div>

        <div className={cn(styles.reviewColumn)}>
          {hasAccess(Permissions.CandidateWrite) && (
            <Select
              className="u-margin-right d-inline-flex "
              inputProps={{ onChange: e => onChangeStatus(check.id, e.target.value) }}
              value={check.user_review_status}
              placeholder="Add Review"
              rounded
              disabledReason="Review status available upon check completion"
              disabled={check.status !== 'completed'}
              small>
              <Select.Item value="approved">Approved</Select.Item>
              <Select.Item value="not_approved">Not Approved</Select.Item>
              <Select.Item value="needs_review">Needs Review</Select.Item>
            </Select>
          )}
        </div>

        <div
          className={cn(
            styles.actionsColumn,
            'd-flex',
            'justify-content-end',
            'align-items-center',
          )}>
          {hasExtraDetails && !check.dummy && (
            <IconComponent
              noBackground
              type="neutral-light"
              icon={File02Icon}
              className="u-margin-right--small"
              size="small"
              tip="More details"
              onClick={() => setShowExtraDetails(true)}
            />
          )}
          {check.completed_at && !check.canceled && check.user_agent && !check.dummy && (
            <IconComponent
              noBackground
              type="neutral-light"
              icon={ComputerIcon}
              className="u-margin-right--small"
              size="small"
              tip="Metadata details"
              onClick={() => setShowMetadata(true)}
            />
          )}
          {check.completed_at &&
            !check.canceled &&
            canAccessResults(true) &&
            checkType.slug !== 'candidate_questionnaire' &&
            !check.dummy &&
            useViewResult && (
              <IconComponent
                noBackground
                type="neutral-light"
                icon={ViewIcon}
                className="u-margin-right--small"
                size="small"
                disabled={check?.result_upload?.blocked}
                tip={check?.result_upload?.blocked ? 'Result upload blocked' : 'View result'}
                onClick={() => checkViewResult(check, 'candidate_check')}
              />
            )}
          {check.completed_at &&
            !check.canceled &&
            canAccessResults() &&
            useDownloadResult &&
            !check.dummy && (
              <IconComponent
                noBackground
                type="neutral-light"
                icon={Download01Icon}
                className="u-margin-right--small"
                size="small"
                disabled={check?.result_upload?.blocked}
                tip={check?.result_upload?.blocked ? 'Result upload blocked' : 'Download result'}
                onClick={downloadResult}
              />
            )}
          {!candidate.completed_at &&
            !check.completed_at &&
            !check.canceled &&
            hasAccess(Permissions.CandidateWrite) &&
            !check.dummy && (
              <IconComponent
                noBackground
                type="neutral-light"
                icon={Cancel01Icon}
                className="u-margin-right--small"
                size="small"
                tip="Cancel Ongoing Check"
                onClick={() => setShowCancelModal(prevState => !prevState)}
              />
            )}
          {check.sub_checks?.length > 0 && !check.dummy && (
            <div className="u-pointer" onClick={() => setSubChecksOpened(curr => !curr)}>
              <IconComponent
                noBackground
                type="neutral-light"
                icon={ArrowDown01Icon}
                className={cn({ [styles.iconButtonRotated]: subChecksOpened })}
                size="small"
              />
              <span className={styles.subCheckExpandIcon}>{check.sub_checks.length}</span>
            </div>
          )}
        </div>
      </div>

      {check.sub_checks?.length > 0 && (
        <Collapse in={subChecksOpened}>
          <div>
            {check.sub_checks.map(subCheck => {
              const subCheckExtraDetails = getExtraDetails(subCheck.extra_details);
              const hasSubCheckExtraDetails =
                subCheckExtraDetails && Object.keys(subCheckExtraDetails).length > 0;

              return (
                <div className={cn(styles.subCheckRow, 'u-divider-top-border', 'u-width-100')}>
                  {hasSubCheckExtraDetails && (
                    <KeyValueModal
                      visible={showSubExtraDetails && selectedSubCheck?.id === subCheck.id}
                      onClose={() => setSubShowExtraDetails(false)}
                      title="More Details"
                      data={subCheckExtraDetails}
                    />
                  )}

                  <div className={cn(styles.titleColumn, 'd-flex', 'align-items-center')}>
                    <div>
                      <div className={styles.dateTitle}>title</div>
                      <div className="title-5">{subCheck.title}</div>
                      <div>
                        <span className={cn(styles.titleStatus, `u-txt-status-${subCheck.status}`)}>
                          {subCheck.status}
                          {(subCheck.status == 'submitted' || subCheck.status == 'in-progress') &&
                            ` - ${subCheck.in_progress_sub_status.replaceAll('_', ' ').capitalize()}`}
                        </span>
                        {subCheck.completed_at &&
                          !subCheck.canceled &&
                          checkType.has_result_flag && (
                            <>
                              <span> • </span>
                              <span
                                className={cn(styles.titleStatus, {
                                  ['u-txt-completed-with-result']: subCheck.result_flag,
                                  ['u-txt-status-completed']: !subCheck.result_flag,
                                })}>
                                {subCheck.result_flag
                                  ? checkType.true_flag_label
                                  : checkType.false_flag_label}
                              </span>
                            </>
                          )}
                      </div>
                    </div>
                  </div>

                  <div className={cn(styles.divider, 'u-divider')} />

                  <div className={cn(styles.createdOnColumn)}>
                    <div className={styles.dateTitle}>
                      {t('check.backgroundChecks.subChecks.columns.createdOn')}
                    </div>
                    <div className={styles.dateValue}>
                      <Moment format={dateSlashFormat}>{subCheck.created_at}</Moment>
                    </div>
                  </div>

                  <div className={cn(styles.completedOnColumn)}>
                    <div className={styles.dateTitle}>
                      {t('check.backgroundChecks.subChecks.columns.completedOn')}
                    </div>
                    <div className={styles.dateValue}>
                      {subCheck.completed_at ? (
                        <Moment format={dateSlashFormat}>{subCheck.completed_at}</Moment>
                      ) : (
                        '-'
                      )}
                    </div>
                  </div>

                  <div className={cn(styles.expiredOnColumn)}>
                    {subCheck.expiration_date && (
                      <>
                        <div className={styles.dateTitle}>
                          {t('check.backgroundChecks.subChecks.columns.expiresOn')}
                        </div>
                        <div className={styles.dateValue}>
                          <Moment format={dateSlashFormat} parse="YYYY-MM-DD">
                            {subCheck.expiration_date}
                          </Moment>
                        </div>
                      </>
                    )}
                  </div>

                  <div className={cn(styles.divider2, 'u-divider')} />

                  <div className={cn(styles.reviewColumn)}>
                    {hasAccess(Permissions.CandidateWrite) && (
                      <Select
                        className="u-margin-right d-inline-flex "
                        inputProps={{
                          onChange: e => onChangeSubCheckStatus(subCheck.id, e.target.value),
                        }}
                        disabledReason="Review status available upon sub-check completion"
                        disabled={subCheck.status !== 'completed'}
                        value={subCheck.user_review_status}
                        placeholder="Add Review"
                        rounded
                        small>
                        <Select.Item value="approved">Approved</Select.Item>
                        <Select.Item value="not_approved">Not Approved</Select.Item>
                        <Select.Item value="needs_review">Needs Review</Select.Item>
                      </Select>
                    )}
                  </div>

                  <div className={cn(styles.actionsColumn, 'd-flex', 'justify-content-end')}>
                    {hasSubCheckExtraDetails && (
                      <IconComponent
                        noBackground
                        type="neutral-light"
                        icon={File02Icon}
                        className="u-margin-right--small"
                        size="small"
                        tip={t('check.backgroundChecks.subChecks.actions.moreDetails')}
                        onClick={() => openSubCheckExtraDetailsModal(subCheck)}
                      />
                    )}
                    {subCheck.completed_at &&
                      !subCheck.canceled &&
                      canAccessResults(true) &&
                      checkType.slug !== 'candidate_questionnaire' &&
                      useViewResult && (
                        <IconComponent
                          noBackground
                          type="neutral-light"
                          icon={ViewIcon}
                          className="u-margin-right--small"
                          size="small"
                          tip={t('check.backgroundChecks.subChecks.actions.viewResult')}
                          onClick={() => {
                            checkViewResult(subCheck, 'sub_check');
                          }}
                        />
                      )}
                    {!subCheck.canceled && canAccessResults() && useDownloadResult && (
                      <IconComponent
                        noBackground
                        type="neutral-light"
                        icon={Download01Icon}
                        className={styles.iconButton}
                        size="small"
                        tip={t('check.backgroundChecks.subChecks.actions.downloadResult')}
                        disabled={!subCheck.completed_at}
                        onClick={() => downloadSubCheckResult(subCheck)}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Collapse>
      )}
    </div>
  );
};
